import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule
} from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
  IconComponent,
  ObserveIntersectingDirective,
  ProgressComponent,
  MessageService
} from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';
import { AdmobService } from '@/core/admod.service';
import { Capacitor } from '@capacitor/core';

import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';
import { ApiService } from '@/core/api.service';
import { CreditService } from '@/main/services/credit.service';
import { Simulation } from '@/main/models/simulation';
import { SimulationService } from '@/main/services/simulation.service';
import { RequestData } from '@/core/models/request-data';
import { environment } from '@/environments/environment';
import { MatSelectModule } from '@angular/material/select';
import { BeneficiaryRepository } from '@/main/repositories/beneficiary-repository';
import { GroupRepository } from '@/main/repositories/group-repository';
import { Beneficiary } from '@/main/models/connection';
import { Group } from '@/main/models/group';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';
import { ConnectionService } from '@/main/services/connection.service';
import { GroupService } from '@/main/services/group.service';

/**
 * @description Componente para activar un crédito
 * @class ActivateCreditComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-activate-credit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    IconComponent,
    ObserveIntersectingDirective,
    ProgressComponent,
    IsGrantedDirective
  ],
  templateUrl: './activate-credit.component.html',
  styleUrl: './activate-credit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivateCreditComponent implements OnInit {
  // Dependency injection
  private readonly admobService = inject(AdmobService);
  private readonly apiService = inject(ApiService);
  public readonly appService = inject(AppService);
  public readonly beneficiaryRepository = inject(BeneficiaryRepository);
  private readonly connectionService = inject(ConnectionService);
  public readonly groupRepository = inject(GroupRepository);
  private readonly groupService = inject(GroupService);
  private readonly sessionService = inject(SessionService);
  private readonly credtiService = inject(CreditService);
  private readonly data: { simulation?: Simulation } = inject(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  public readonly simulationService = inject(SimulationService);

  // Events
  public readonly afterSubmit = output<Operation>();

  // Properties
  public attachedFiles = signal<File[]>([]);
  public beneficiaries = this.beneficiaryRepository.findBy();
  public groups = this.groupRepository.findBy();
  public currencyId = signal<string>('');
  public form: FormGroup = new FormGroup({});
  public loading = signal<boolean>(false);
  public attachingFile = signal<boolean>(false);
  public readonly today = new Date();
  public readonly totalToReceive = $localize`Total to receive`;
  public submitting = signal<boolean>(false);
  public simulation = signal<Simulation | undefined>(undefined);
  public attachmentMaxSize = signal<number>(0);

  /**
   * @description Constructor del componente
   * @constructor
   */
  constructor() {
    this.initForm();
  }

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  ngOnInit(): void {
    this.initializeSettings();
    this.loadProperties();
    if (Capacitor.getPlatform() !== 'web') {
      this.admobService.init();
    }
  }

  async openBeneficiary(): Promise<void> {
    const operation = await this.connectionService.openBeneficiary();
    if (operation) {
      this.beneficiaries.reload();
      this.form.get('beneficiary')?.setValue(operation.entity as Beneficiary);
    }
  }
  async openGroup(): Promise<void> {
    const operation = await this.groupService.open();
    if (operation) {
      this.groups.reload();
      this.form.get('group')?.setValue(operation.entity as Group);
    }
  }

  /**
   * @description Adjunta un documento a la simulación
   * @param {Simulation | undefined} simulation - Simulación a la que adjuntar el documento
   * @returns {Promise<void>}
   */
  public async attachDocument(
    simulation: Simulation | undefined
  ): Promise<void> {
    try {
      if (simulation) {
        this.attachingFile.set(true);
        this.form.disable();
        setTimeout(() => {
          this.attachingFile.set(false);
          this.form.enable();
        }, 5000);
        this.attachedFiles.set(
          (await this.credtiService.attachDocument(simulation)) || []
        );
      }
    } finally {
      this.attachingFile.set(false);
      this.form.enable();
    }
  }

  compareItem(a: any, b: any): boolean {
    return a && b ? a.sequence === b.sequence : a === b;
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private initForm(): void {
    this.form = this.formBuilder.group({
      operationNumber: null,
      beneficiary: [null as Beneficiary | null, Validators.required],
      group: [null as Group | null],
      notifyOverduePayment: false,
      notifyPayment: false,
      notifySendingReceipts: false
    });

    if (this.data.simulation?.beneficiary) {
      this.form.patchValue(this.data.simulation);
    }
  }

  /**
   * @description Inicializa la configuración del componente
   * @private
   */
  private initializeSettings(): void {
    this.attachmentMaxSize.set(
      Number(
        this.sessionService
          .settings()
          ?.validations.find(v => v.id.trim() === 'MPTMX')?.value
      ) || 0
    );
    this.simulation.set(this.data.simulation);
    const countries = this.sessionService
      .catalogs()
      .filter(c => c.catalogId === 'CPAMD');
    this.currencyId.set(
      countries?.find(c => c.id === this.data?.simulation?.countryId)?.value ||
        ''
    );
  }

  private async loadProperties(): Promise<void> {
    const lenderId = this.sessionService.settings()?.lenderId;
    if (lenderId) {
      await Promise.all([
        this.beneficiaries.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        }),
        this.groups.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        })
      ]);
    }
  }

  /**
   * @description Mapea los datos de la solicitud
   * @param {any} data - Datos a mapear
   * @returns {any} Datos mapeados
   */
  public mapRequest(data: any): any {
    let request = structuredClone(data);
    request.beneficiary.alias = request.beneficiary.alias || request.beneficiary.description;
    request.countryId = this.data.simulation?.countryId;
    request.productTypeId = 'CON';
    if (request.beneficiary.email?.trim() === '') {
      request.notifyOverduePayment = false;
      request.notifyPayment = false;
      request.notifySendingReceipts = false;
    } else {
      request.beneficiary.notifyOverduePayment = request.notifyOverduePayment;
      request.beneficiary.notifyPayment = request.notifyPayment;
      request.beneficiary.notifySendingReceipts = request.notifySendingReceipts;
      delete request.notifyOverduePayment;
      delete request.notifyPayment;
      delete request.notifySendingReceipts;
    }
    if (!request.group) {
      request.group = { sequence: null };
    }
    return request;
  }

  /**
   * @description Envía el formulario para activar el crédito
   * @returns {Promise<void>}
   */
  public async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        const data: RequestData = {
          microServiceName: 'WSMPCreditManagement',
          resourcePath: `/offer/activate-credit`,
          method: 'POST',
          useToken: true,
          body: this.mapRequest(this.form.value),
          bodyEncrypted: true
        };
        this.form.disable();
        this.submitting.set(true);
        await this.apiService.call(data);
        this.dialog.closeAll();
        this.messageService.show(
          $localize`A new credit has been created successfully.`,
          { class: 'ft-message--success', icon: 'check--circle' }
        );
        // Solo muestra publicidad en el plan gratuito
        if (
          this.sessionService.settings()?.validations.some(v => v.id === 'MPDPC' && v.value === 'S')
        ) {
          if (Capacitor.getPlatform() === 'android') {
            await this.admobService.showInterstitial(
              environment.admob.android.activateCreditUnitCode
            );
          } else if (Capacitor.getPlatform() === 'ios') {
            await this.admobService.showInterstitial(
              environment.admob.ios.activateCreditUnitCode
            );
          }
        }
        this.router.navigateByUrl('/credits');
      } finally {
        this.form.enable();
        this.submitting.set(false);
      }
    }
  }
}
